/**
 * The label/value row and the count badge Gate Detail and the Access page both draw.
 *
 * **One copy, because D265 split the screen that had the only copy.** Both were local to
 * `GateDetailScreen.tsx` when two call sites on one screen was the whole story; D265 moved half
 * the rows to a second screen, and two byte-identical local components is the shape
 * `detailFromCache`'s own docblock warns about — a field rename applied to each is where a
 * divergence starts.
 *
 * **Still P7's, and deliberately not promoted to `components/`.** `AGENTS.md` §4.8's rule is
 * *anything a screen PRD would not have to build is the shell's*; P7 would build both of these,
 * and both consumers are P7 screens. `screens/shared/` is where a thing two P7 screens share
 * belongs — the same reason `access.ts` and `api.ts` are there.
 */
import type { ReactNode } from 'react'
import { Icon } from '../../components'

/**
 * A `<button>` with a chevron when an `onClick` is supplied, a plain `<div>` without one — which
 * is what makes a row's navigability a prop rather than a second component (D246 removed the
 * subscription row's destination by dropping its `onClick`, and nothing else).
 */
export function DetailRow({
  label,
  value,
  onClick,
}: {
  readonly label: string
  readonly value: ReactNode
  onClick?: () => void
}): JSX.Element {
  const content = (
    <>
      <span className="gate-row-label">{label}</span>
      <span className="gate-row-value">
        {value} {onClick !== undefined ? <Icon name="chevron-right" size={14} /> : null}
      </span>
    </>
  )
  if (onClick === undefined) return <div className="gate-detail-row">{content}</div>
  return (
    <button className="gate-detail-row" type="button" onClick={onClick}>
      {content}
    </button>
  )
}

/**
 * B3 — neutral `count-badge` base plus a `.green`/`.amber`/`.red` modifier; only the Subscription
 * row ever passes a `variant`.
 */
export function CountBadge({
  children,
  variant,
}: {
  readonly children: ReactNode
  readonly variant?: 'green' | 'amber' | 'red'
}): JSX.Element {
  return <span className={`count-badge${variant === undefined ? '' : ` ${variant}`}`}>{children}</span>
}
